import React, { useCallback, useEffect, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { Link } from "react-router-dom";
import { IFeedItem } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import Button from "src/components/Button/Button";

import ClubPostCard from "./FeedItem/ClubPostCard";
import AchievementCard from "./FeedItem/AchievementCard";
import OwnerPostCard from "./FeedItem/OwnerPostCard";

import styles from "./Feed.module.scss";

/** Server page size; a full page means there may be more. */
const PAGE_SIZE = 30;

/**
 * Where the items come from. Both sources go through the same API
 * assembler, so the cards and their actions are identical; only the query
 * and the empty state differ.
 */
export type FeedSource =
    | { kind: "home" }
    | { kind: "dog"; dogId: string; isOwner: boolean; dogName: string }
    | { kind: "club"; clubId: string; isManager: boolean; clubName: string };

interface IProps {
    source?: FeedSource;
}

const Feed: React.FC<IProps> = ({ source = { kind: "home" } }) => {
    const user = helperService.getUser();
    const [items, setItems] = useState<IFeedItem[] | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const sourceKey = source.kind === "dog"
        ? `dog:${source.dogId}`
        : source.kind === "club"
            ? `club:${source.clubId}`
            : "home";
    const fetchPage = useCallback((before?: string) => {
        if (source.kind === "dog") return apiService.getDogFeed(source.dogId, { before });
        if (source.kind === "club") return apiService.getClubFeed(source.clubId, { before });
        return apiService.getFeed({ before });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceKey]);

    const load = useCallback(() => {
        fetchPage().then((page) => {
            setItems(page);
            setHasMore(page.length >= PAGE_SIZE);
        }).catch(() => {
            setItems([]);
            setHasMore(false);
        });
    }, [fetchPage]);

    const loadMore = () => {
        if (!items || items.length === 0 || loadingMore) return;
        setLoadingMore(true);
        const last = items[items.length - 1];
        fetchPage(new Date(last.created_at).toISOString()).then((page) => {
            setItems((current) => [...(current || []), ...page]);
            setHasMore(page.length >= PAGE_SIZE);
        }).catch(() => {
            setHasMore(false);
        }).finally(() => setLoadingMore(false));
    };

    // Home is for the signed-in user; dog and club activity are public pages.
    const canLoad = source.kind !== "home" || !!user._id;

    useEffect(() => {
        if (!canLoad) return;
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canLoad, load]);

    if (!canLoad || !items) return null;

    if (items.length === 0) {
        if (source.kind === "club") {
            return source.isManager ? (
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>Nothing here yet</div>
                    <div className={styles.emptyBody}>
                        {source.clubName}'s published events and updates will show up here.
                        Post an update above to get started.
                    </div>
                </div>
            ) : (
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>No activity yet</div>
                    <div className={styles.emptyBody}>
                        {source.clubName} hasn't posted anything on Eager Dog yet.
                    </div>
                </div>
            );
        }
        if (source.kind === "dog") {
            return source.isOwner ? (
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>Nothing here yet</div>
                    <div className={styles.emptyBody}>
                        {source.dogName}'s entries, results, runs, and photos will show up here.
                        Share a photo or log a run above to get started.
                    </div>
                </div>
            ) : (
                <div className={styles.empty}>
                    <div className={styles.emptyTitle}>No activity yet</div>
                    <div className={styles.emptyBody}>
                        {source.dogName} hasn't been entered in anything on Eager Dog yet.
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.empty}>
                <div className={styles.emptyTitle}>Nothing in your feed yet</div>
                <div className={styles.emptyBody}>
                    Share a photo or run result above, or follow a club to see their updates here.
                </div>
                <Link to="/kennel-clubs" className={styles.emptyCta}>Browse clubs to follow</Link>
            </div>
        );
    }

    return (
        <div className={styles.Feed}>
            {items.map((item) => {
                if (item.type === Constants.feed_item_type.achievement) {
                    return <AchievementCard key={item.id} item={item} />;
                }
                if (item.author_type === Constants.post_author_type.club) {
                    return <ClubPostCard key={item._id} post={item} onChange={load} />;
                }
                return <OwnerPostCard key={item._id} post={item} onChange={load} />;
            })}
            {hasMore && (
                <div className={styles.loadMore}>
                    <Button variant="secondary" onClick={loadMore} isLoading={loadingMore}>
                        Load more
                    </Button>
                </div>
            )}
        </div>
    );
};

export default Feed;
